import React, {
  useImperativeHandle,
  forwardRef,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import { ITouchEvent, View } from "@tarojs/components";
import { XHComponentCommonProps } from "../../types";
import Keyboard from "./Keyboard";
import { isLicenseNo, replaceCharByIndex } from "../../utils";

import "./index.scss";

export type LicensePlateProps = {
  value: string;
  onChange: (index: string) => void;
  /**
   * 是否默认展示键盘
   */
  showKeyboard?: boolean;
  closeText?: string;
  label?: ReactNode;
  onFinish?: () => void;
  padding?: boolean;
  border?: boolean;
  autoScroll?: boolean;
  dataTrackId?: string;
} & XHComponentCommonProps;

const LENGTH = 8;

export type LicensePlateInputRef = {
  /**
   * 是否有效，组件会用大写字母（O) 来代替空位，如果要自己校验，那么就得判断有没有“O”来判断有没有空格
   */
  isValid: boolean;
};

const LicensePlateInput = forwardRef<LicensePlateInputRef, LicensePlateProps>(
  (props, fromRef) => {
    const {
      value = "",
      onChange,
      className,
      showKeyboard = false,
      padding = false,
      border = false,
      onFinish,
      autoScroll = true,
      label,
      dataTrackId = "LicensePlateInput",
      ...rest
    } = props;

    const [activeIndex, setActiveIndex] = useState(value.length);

    const [show, setShowKeyboard] = useState(showKeyboard);

    const InputRef = useRef<HTMLDivElement>(null);

    /**
     * 空位用“O”代替(大写字母 O)
     * @param neVal 新值
     */
    const handleOnChage = neVal => {
      setActiveIndex(old => {
        if (neVal.replace(/O/g, "").length >= LENGTH) return old;
        return old + 1;
      });
      // if (neVal.length === LENGTH) {
      //   onFinish?.();
      //   setShowKeyboard(false);
      // }
      onChange(neVal);
    };

    const handleKeyClick = (index: number) => {
      if (index > value.length - 1) return;
      setActiveIndex(index);
    };

    useImperativeHandle(
      fromRef,
      () => ({
        isValid: isLicenseNo(value),
      }),
      [value]
    );

    function renderInputs(): ReactNode {
      const Inputs: any = [];

      for (let i = 0; i < LENGTH; i++) {
        const char = value[i];

        Inputs.push(
          <View
            key={i}
            className={classNames("xh-licensePlate-input-item", {
              active: activeIndex === i,
              showNew: i === LENGTH - 1 && value.length !== LENGTH,
            })}
            // @ts-ignore
            dataTrackId={`${dataTrackId}-input-item-${i}`}
            onClick={() => handleKeyClick(i)}
          >
            {char === "O" ? "" : char}
          </View>
        );
      }

      return Inputs;
    }

    /**
     * 删除其中一位
     * 如果删除那一位后面有值就用“O”代替删除的那一位，否则直接删除
     * 只要当前value有值就把 index-1
     */
    const handleDelete = () => {
      const nextChar = value[activeIndex + 1];
      const replaceChar = nextChar ? "O" : "";
      const newValue = replaceCharByIndex(value, activeIndex, replaceChar);

      if (newValue.length !== 0) setActiveIndex(old => old - 1);
      onChange(newValue);
    };

    const handleClose = () => {
      setShowKeyboard(false);
    };

    useEffect(() => {
      if (
        show &&
        InputRef.current &&
        autoScroll &&
        process.env.TARO_ENV === "h5"
      ) {
        InputRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, [show, autoScroll]);

    function handlerClick(e: ITouchEvent): void {
      e.stopPropagation();
      setShowKeyboard(true);
    }
    const xhLicensePlateCls = classNames(
      "xh-licensePlate",
      className,
      { padding },
      { border }
    );

    return (
      <View
        className={xhLicensePlateCls}
        // @ts-ignore
        dataTrackId={dataTrackId}
      >
        {label && <label className="xh-licensePlate-label">{label}</label>}
        <View
          className="xh-licensePlate-input"
          onClick={handlerClick}
          // @ts-ignore
          dataTrackId={`${dataTrackId}-input`}
          ref={InputRef}
        >
          {renderInputs()}
        </View>
        <Keyboard
          activeIndex={activeIndex}
          dataTrackId={dataTrackId}
          show={show}
          value={value}
          onChange={handleOnChage}
          onDelete={handleDelete}
          onClose={handleClose}
          {...rest}
        />
      </View>
    );
  }
);

export default LicensePlateInput;
